import { useMemo, useState, useCallback } from "react";
import { useJourneyApi } from "./Journey.api";
import { usePagesApi } from "@/v2/features/pages/Pages.api";
import JourneySankeyChart from "./JourneySankeyChart";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { Flex, Spinner, Text, Separator } from "@radix-ui/themes";
import { Route01 } from "@/v2/assets/icons/Route01";
import {
  DateRangePicker,
  DateRange,
} from "@/components/shared/date-range-picker/DateRangePicker";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { Slider } from "@/components/ui/slider";
import Select from "@/components/shared/select";
import { useDebounce } from "@/v2/shared/hooks/useDebounce";
import { GetJourneyStatsResponse, JourneyLink } from "./Journey.model";
import styles from "./Journey.module.scss";

type ChartData = {
  nodes: { id: string }[];
  links: JourneyLink[];
  numberOfPages: number;
  numberOfUserNavigations: number;
  numberOfSessions: number;
  numberOfPagesBeforeThreshold: number;
  numberOfUserNavigationsBeforeThreshold: number;
  numberOfSessionsBeforeThreshold: number;
};

function responseToChartData(data: GetJourneyStatsResponse): ChartData {
  const removeBaseUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname + urlObj.search + urlObj.hash;
      return path.startsWith("/") ? path.slice(1) : path;
    } catch {
      const baseUrlPatterns = [/^https?:\/\/[^/]+/, /^http:\/\/localhost:\d+/];
      for (const pattern of baseUrlPatterns) {
        if (pattern.test(url)) {
          const result = url.replace(pattern, "");
          return result.startsWith("/") ? result.slice(1) : result;
        }
      }
      return url;
    }
  };

  const transitions = data.transitions || [];
  const transformedLinks = transitions.map((link) => ({
    ...link,
    source: removeBaseUrl(link.source),
    target: removeBaseUrl(link.target),
  }));

  const nodeIds = new Set<string>();
  transformedLinks.forEach((link) => {
    nodeIds.add(link.source);
    nodeIds.add(link.target);
  });

  const nodes = Array.from(nodeIds).map((id) => ({ id }));

  return {
    nodes,
    links: transformedLinks,
    numberOfPages: data.numberOfPages ?? 0,
    numberOfUserNavigations: data.numberOfUserNavigations ?? 0,
    numberOfSessions: data.numberOfSessions ?? 0,
    numberOfPagesBeforeThreshold: data.numberOfPagesBeforeThreshold ?? 0,
    numberOfUserNavigationsBeforeThreshold:
      data.numberOfUserNavigationsBeforeThreshold ?? 0,
    numberOfSessionsBeforeThreshold: data.numberOfSessionsBeforeThreshold ?? 0,
  };
}

type ViewMode = "navigation" | "quackchatInit";

const Journey = () => {
  const journeyApi = useJourneyApi();
  const pagesApi = usePagesApi();

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    dateFrom: dayjs().startOf("day").subtract(7, "day").toISOString(),
    dateTo: dayjs().toISOString(),
  }));

  const [viewMode, setViewMode] = useState<ViewMode>("navigation");
  const [selectedPageId, setSelectedPageId] = useState<string>("");
  const [thresholdDefinition, setThresholdDefinition] = useState<
    "higher" | "lower"
  >("higher");
  const [thresholdNavigationNumber, setThresholdNavigationNumber] = useState<
    number[]
  >([1]);

  const debouncedThresholdNavigationNumber = useDebounce(
    thresholdNavigationNumber[0],
    1000
  );

  const queryParams = useMemo(
    () => ({
      startDate: dateRange.dateFrom,
      endDate: dateRange.dateTo,
      thresholdDefinition,
      thresholdNavigationNumber: debouncedThresholdNavigationNumber,
    }),
    [
      dateRange.dateFrom,
      dateRange.dateTo,
      thresholdDefinition,
      debouncedThresholdNavigationNumber,
    ]
  );

  const handleDateRangeChange = useCallback((newDateRange: DateRange) => {
    setDateRange({
      dateFrom: newDateRange.dateFrom,
      dateTo: dayjs().toISOString(),
    });
  }, []);

  const handleViewModeChange = useCallback((value: string) => {
    if (value === "navigation" || value === "quackchatInit") {
      setViewMode(value);
      if (value === "navigation") {
        setSelectedPageId("");
      }
    }
  }, []);

  const handlePageSelect = useCallback((pageId: string) => {
    setSelectedPageId(pageId);
  }, []);

  const handleThresholdDefinitionChange = useCallback((value: string) => {
    if (value === "higher" || value === "lower") {
      setThresholdDefinition(value);
    }
  }, []);

  const handleThresholdNavigationNumberChange = useCallback(
    (value: number[]) => {
      setThresholdNavigationNumber(value);
    },
    []
  );

  const { data, isLoading } = useQuery({
    queryKey: ["journeys", queryParams],
    queryFn: () => journeyApi.getJourniesData(queryParams),
    refetchOnWindowFocus: false,
    enabled: viewMode === "navigation",
  });

  const { data: pages, isLoading: isPagesLoading } = useQuery({
    queryKey: ["pages", dateRange.dateFrom, dateRange.dateTo],
    queryFn: () =>
      pagesApi.getPages({
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateTo,
      }),
    refetchOnWindowFocus: false,
  });

  const quackchatInitQueryParams = useMemo(
    () => ({
      ...queryParams,
      pageId: selectedPageId || undefined,
    }),
    [queryParams, selectedPageId]
  );

  const { data: quackchatInitData, isLoading: isQuackchatInitLoading } =
    useQuery({
      queryKey: ["journeys-quackchat-init", quackchatInitQueryParams],
      queryFn: () => journeyApi.getQuackchatInitData(quackchatInitQueryParams),
      refetchOnWindowFocus: false,
      enabled: viewMode === "quackchatInit" && selectedPageId.length > 0,
    });

  const chartData = useMemo(
    () => (data ? responseToChartData(data) : null),
    [data]
  );

  const quackchatInitChartDataList = useMemo(() => {
    if (quackchatInitData == null) return [];
    let list: GetJourneyStatsResponse[];
    if (Array.isArray(quackchatInitData)) {
      list = quackchatInitData;
    } else if (
      typeof quackchatInitData === "object" &&
      "data" in quackchatInitData &&
      Array.isArray((quackchatInitData as { data: GetJourneyStatsResponse[] }).data)
    ) {
      list = (quackchatInitData as { data: GetJourneyStatsResponse[] }).data;
    } else if (
      typeof quackchatInitData === "object" &&
      "transitions" in quackchatInitData
    ) {
      list = [quackchatInitData as GetJourneyStatsResponse];
    } else {
      list = [];
    }
    return list.map((entry) => responseToChartData(entry));
  }, [quackchatInitData]);

  const pageOptions = useMemo(
    () =>
      (pages ?? []).map((p) => ({
        label: `${p.name} - ${p.count}`,
        value: p.id,
      })),
    [pages]
  );

  const isLoadingCharts =
    viewMode === "navigation" ? isLoading : isQuackchatInitLoading;

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Journeys"
          description="Visualize user journey flows and navigation patterns through your application."
          icon={
            <Route01
              size={24}
              fill={PageColors[ENavigate.JOURNEYS] || "gray"}
            />
          }
          actions={
            <Flex gap="4" align="center">
              <Flex direction="column" gap="2">
                <Text size="2" color="gray">
                  Mode
                </Text>
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={handleViewModeChange}
                >
                  <ToggleGroupItem value="navigation" aria-label="Navigation">
                    Navigation
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="quackchatInit"
                    aria-label="Quackchat Init"
                  >
                    Quackchat Init
                  </ToggleGroupItem>
                </ToggleGroup>
              </Flex>
              <Flex direction="column" gap="2">
                <Text size="2" color="gray">
                  Threshold Definition
                </Text>
                <ToggleGroup
                  type="single"
                  value={thresholdDefinition}
                  onValueChange={handleThresholdDefinitionChange}
                >
                  <ToggleGroupItem value="higher" aria-label="Higher">
                    Higher
                  </ToggleGroupItem>
                  <ToggleGroupItem value="lower" aria-label="Lower">
                    Lower
                  </ToggleGroupItem>
                </ToggleGroup>
              </Flex>
              <Flex
                direction="column"
                gap="2"
                className={styles.sliderContainer}
              >
                <Text size="2" color="gray">
                  Threshold Navigation: {thresholdNavigationNumber[0]}%
                </Text>
                <Slider
                  value={thresholdNavigationNumber}
                  onValueChange={handleThresholdNavigationNumberChange}
                  min={0.1}
                  max={10}
                  step={0.1}
                />
              </Flex>
              {viewMode === "quackchatInit" && (
                <Flex direction="column" gap="2">
                  <Text size="2" color="gray">
                    Page
                  </Text>
                  <Select
                    options={pageOptions}
                    value={selectedPageId}
                    onChange={handlePageSelect}
                    placeholder="Select page"
                    loading={isPagesLoading}
                    disabled={isPagesLoading}
                  />
                </Flex>
              )}
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </Flex>
          }
        />
        {viewMode === "navigation" && chartData && (
          <Flex className={styles.header} direction="row" gap="6" px="6" py="4">
            <Flex direction="column" gap="2" className={styles.headerSection}>
              <Text size="2" color="gray" highContrast>
                Total Navigations:{" "}
                {chartData.numberOfUserNavigationsBeforeThreshold}
              </Text>
              <Separator orientation="horizontal" size="4" />

              <Text size="2" color="gray" highContrast>
                Total Sessions: {chartData.numberOfSessionsBeforeThreshold}
              </Text>
              <Separator orientation="horizontal" size="4" />
              <Text size="2" color="gray" highContrast>
                Total Pages: {chartData.numberOfPagesBeforeThreshold}
              </Text>
            </Flex>
            <Flex direction="column" gap="2" className={styles.headerSection}>
              <Text size="2" color="gray" highContrast>
                Navigations that met the threshold:{" "}
                {chartData.numberOfUserNavigations}
              </Text>
              <Separator orientation="horizontal" size="4" />

              <Text size="2" color="gray" highContrast>
                Sessions that met the threshold: {chartData.numberOfSessions}
              </Text>
              <Separator orientation="horizontal" size="4" />
              <Text size="2" color="gray" highContrast>
                Pages that met the threshold: {chartData.numberOfPages}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Flex px="6" direction="column">
        {isLoadingCharts ? (
          <Flex justify="center" align="center" py="9">
            <Spinner size="3" />
          </Flex>
        ) : viewMode === "navigation" ? (
          !chartData || chartData.nodes.length < 2 ? (
            <Flex justify="center" align="center" py="9">
              <Text size="2" color="gray" highContrast>
                No data available
              </Text>
            </Flex>
          ) : (
            <div className={styles.container}>
              <JourneySankeyChart
                nodes={chartData.nodes}
                links={chartData.links}
              />
            </div>
          )
        ) : !selectedPageId ? (
          <Flex justify="center" align="center" py="9">
            <Text size="2" color="gray" highContrast>
              Select a page to view stats
            </Text>
          </Flex>
        ) : quackchatInitChartDataList.length === 0 ? (
          <Flex justify="center" align="center" py="9">
            <Text size="2" color="gray" highContrast>
              No data available
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="6">
            {quackchatInitChartDataList.map((entryChartData, index) => (
              <Flex key={index} direction="column" gap="2">
                <Flex
                  className={styles.header}
                  direction="row"
                  gap="6"
                  px="6"
                  py="4"
                >
                  <Flex
                    direction="column"
                    gap="2"
                    className={styles.headerSection}
                  >
                    <Text size="2" color="gray" highContrast>
                      Total Navigations:{" "}
                      {entryChartData.numberOfUserNavigationsBeforeThreshold}
                    </Text>
                    <Separator orientation="horizontal" size="4" />

                    <Text size="2" color="gray" highContrast>
                      Total Sessions:{" "}
                      {entryChartData.numberOfSessionsBeforeThreshold}
                    </Text>
                    <Separator orientation="horizontal" size="4" />
                    <Text size="2" color="gray" highContrast>
                      Total Pages:{" "}
                      {entryChartData.numberOfPagesBeforeThreshold}
                    </Text>
                  </Flex>
                  <Flex
                    direction="column"
                    gap="2"
                    className={styles.headerSection}
                  >
                    <Text size="2" color="gray" highContrast>
                      Navigations that met the threshold:{" "}
                      {entryChartData.numberOfUserNavigations}
                    </Text>
                    <Separator orientation="horizontal" size="4" />

                    <Text size="2" color="gray" highContrast>
                      Sessions that met the threshold:{" "}
                      {entryChartData.numberOfSessions}
                    </Text>
                    <Separator orientation="horizontal" size="4" />
                    <Text size="2" color="gray" highContrast>
                      Pages that met the threshold:{" "}
                      {entryChartData.numberOfPages}
                    </Text>
                  </Flex>
                </Flex>
                {entryChartData.nodes.length < 2 ? (
                  <Flex
                    justify="center"
                    align="center"
                    py="6"
                    className={styles.container}
                  >
                    <Text size="2" color="gray">
                      No data for this entry
                    </Text>
                  </Flex>
                ) : (
                  <div className={styles.container}>
                    <JourneySankeyChart
                      nodes={entryChartData.nodes}
                      links={entryChartData.links}
                    />
                  </div>
                )}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </PageLayoutV2>
  );
};

export default Journey;
